import { createHttpClient, createRequestBuilder, HttpRequest, HttpResponse } from '@quickplay/foundation';

/**
 * Fetches a MediaTailor response from the specified URL.
 * @param url The URL to fetch the MediaTailor response from.
 * @template T The expected type of the response body.
 * @description Fetches a MediaTailor response from the specified URL with optional query parameters and body.
 * Supports both GET and POST methods.
 * If the response code is less than 400, it resolves with the response body; otherwise, it rejects with the error code.
 * @param options Optional parameters for the request, including query parameters and body.
 * @param options.queryParams Optional query parameters to include in the request.
 * @param options.body Optional body to include in the request for POST method.
 * @param method The HTTP method to use for the request.
 * @returns A promise that resolves with the response body or rejects with an error code.
 */
export function fetchMediaTailorResponse<T>(
  url: string,
  options?: {
    queryParams?: { [key: string]: any };
    body?: { [key: string]: any };
  },
  connectionTimeoutInMS?: number,
  method: 'GET' | 'POST' = 'GET',
): Promise<T> {
  const DEFAULT_CONNECTION_TIMEOUT_MS = 8_000;
  return new Promise((resolve, reject) => {
    const requestBuilder = createRequestBuilder()
      .url(url)
      .options({ disableEncode: true })
      .responseFormat('json')
      .queryParameters(options?.queryParams || {})
      .timeout(connectionTimeoutInMS ?? DEFAULT_CONNECTION_TIMEOUT_MS);

    if (method === 'POST') {
      requestBuilder.post(options?.body);
    } else {
      requestBuilder.get();
    }

    const request: HttpRequest<any> = requestBuilder.build();
    const httpClient = createHttpClient<any, string>();
    httpClient
      .callWith(request)
      .then((response: HttpResponse<any>) => {
        if (response.body && response.code < 400) {
          resolve(response.body);
        } else {
          reject(response.code);
        }
      })
      .catch((error: any) => {
        reject(error);
      });
  });
}

/** * Converts a DASH time string (e.g., "PT1H30M15S") to epoch seconds.
 * @param time The DASH time string to convert.
 * @returns The equivalent time in seconds.
 */
export function convertISODurationToEpochSeconds(time: string): number {
  const match = time.match(/^PT(?:(\d{1,4})H)?(?:(\d{1,2})M)?(?:(\d{1,2}(?:\.\d{1,3})?)S)?$/);

  if (!match) {
    throw new Error('Invalid DASH time format');
  }

  const hours = parseInt(match[1] || '0', 10);
  const minutes = parseInt(match[2] || '0', 10);
  const seconds = parseFloat(match[3] || '0');

  return hours * 3600 + minutes * 60 + seconds;
}

/**
 * Converts a time string in the format HH:MM:SS to seconds.
 * @param time The time string to convert.
 * @returns The equivalent time in seconds.
 */
export function convertTimeStringToSeconds(time: string): number {
  const match = time.match(/^(\d{1,2}):(\d{1,2}):(\d{1,2})$/);
  if (!match) {
    throw new Error('Invalid time format');
  }
  const hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const seconds = parseInt(match[3], 10);
  return hours * 3600 + minutes * 60 + seconds;
}
